import {
  Button,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Tooltip,
  Input,
  Select,
  SelectItem,
} from "@nextui-org/react";
import React, { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import fetchWrapper from "../utils/fetch";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import debounce from 'lodash/debounce';
import { getRelativeTime, formatDateTime } from "../utils/timeUtils";

const defaultFilters = {
  search: '',
  sort_by: 'created_at',
  sort_order: 'desc',
  category: ''
};

// Custom hook for fetching decisions
const useDecisions = (deletedCount, page, filters) => {
  const [isLoading, setIsLoading] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [totalDecisions, setTotalDecisions] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDecisions = async () => {
      setIsLoading(true);
      try {
        const data = await fetchWrapper.get(`/get/problems?${new URLSearchParams({
          page,
          search: filters.search || '',
          sort_by: filters.sort_by,
          sort_order: filters.sort_order,
          category: filters.category || ''
        }).toString()}`);
        setDecisions(data.problems);
        setTotalDecisions(data.total);
      } catch (err) {
        handleError(err, navigate);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDecisions();
  }, [deletedCount, page, filters, navigate]);

  return { isLoading, decisions, totalDecisions };
};

// Custom hook for deleting decisions
const useDeleteDecision = (setDeletedCount) => {
  const navigate = useNavigate();

  const deleteDecision = async (uid) => {
    const confirmed = window.confirm("آیا مطمئن هستید ؟");
    if (!confirmed) return;

    try {
      await fetchWrapper.post("/problem/delete", { problem_uid: uid });
      setDeletedCount((d) => d + 1);
      navigate("/my-decisions-v2");
    } catch (err) {
      handleError(err, navigate);
    }
  };

  return { deleteDecision };
};

// Error handling utility
const handleError = (err, navigate) => {
  if (err?.detail === "not found") {
    toast.error("این تمپلیت موجود نیست");
    navigate("/");
  } else if (err?.detail === "Invalid credentials") {
    toast.error("نام کاربری یا رمز عبور درست نیست.");
    navigate("/login");
  } else {
    toast.error(err);
  }
};

// Action Menu Component
const ActionMenu = ({ decision, onDelete, onViewResults }) => {
  const shareUrl = `${window.location.protocol}//${window.location.host}/problem/create?template_id=${decision.problem_uid}`;

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button variant="flat" color="primary">
          عملیات
        </Button>
      </DropdownTrigger>
      <DropdownMenu aria-label="عملیات">
        <DropdownItem
          key="view"
          onClick={() => onViewResults(decision.problem_uid)}
        >
          نمایش نتایج
        </DropdownItem>
        <DropdownItem
          key="edit"
          as={Link}
          to={`/problem/create?template_id=${decision.problem_uid}`}
        >
          ویرایش
        </DropdownItem>
        <DropdownItem
          key="share"
          onPress={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success("لینک اشتراک‌گذاری با موفقیت کپی شد.");
          }}
        >
          کپی لینک اشتراک
        </DropdownItem>
        <DropdownItem
          key="delete"
          className="text-danger"
          color="danger"
          onClick={() => onDelete(decision.problem_uid)}
        >
          حذف
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

// Main Component
export default function MyDecisionsV2() {
  const [{ isAuthenticated, requested }] = useAuth();
  const [deletedCount, setDeletedCount] = useState(0);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  // Initialize filters
  const [filters, setFilters] = useState(defaultFilters);
  
  const { isLoading, decisions, totalDecisions } = useDecisions(deletedCount, page, filters);
  const { deleteDecision } = useDeleteDecision(setDeletedCount);

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: searchText
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDelete = async (uid) => {
    const confirmed = window.confirm("آیا مطمئن هستید ؟");
    if (!confirmed) return;

    try {
      await fetchWrapper.post("/problem/delete", { problem_uid: uid });
      toast.success("تصمیم با موفقیت حذف شد");
      setDeletedCount(prev => prev + 1);
    } catch (err) {
      handleError(err, navigate);
    }
  };

  const handleViewResults = (problemId) => {
    navigate(`/problem/result?problem_id=${problemId}`);
  };

  const renderFilters = () => (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="flex gap-2">
        <Input
          type="text"
          label="جستجو"
          placeholder="جستجو در نام تصمیم"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyPress={handleKeyPress}
          endContent={
            <Button
              isIconOnly
              variant="light"
              size="sm"
              onClick={handleSearch}
              className="text-default-400 hover:text-default-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            </Button>
          }
        />
      </div>

      <Select
        label="دسته‌بندی"
        selectedKeys={[filters.category]}
        value={filters.category}
        onChange={(e) => {
          setFilters({
            ...filters,
            category: e.target.value
          });
        }}
        items={[
          { key: "", label: "همه دسته‌ها" },
          { key: "shared", label: "عمومی" },
          { key: "public", label: "عمومی (ادمین)" },
          { key: "private", label: "خصوصی" }
        ]}
      >
        {(item) => (
          <SelectItem key={item.key} value={item.key}>
            {item.label}
          </SelectItem>
        )}
      </Select>

      <Select
        label="مرتب‌سازی"
        selectedKeys={[`${filters.sort_by}-${filters.sort_order}`]}
        value={`${filters.sort_by}-${filters.sort_order}`}
        onChange={(e) => {
          const [sort_by, sort_order] = e.target.value.split('-');
          setFilters({
            ...filters,
            sort_by,
            sort_order
          });
        }}
        items={[
          { key: "created_at-desc", label: "جدیدترین" },
          { key: "created_at-asc", label: "قدیمی‌ترین" },
          { key: "problem_name-asc", label: "نام تصمیم (صعودی)" },
          { key: "problem_name-desc", label: "نام تصمیم (نزولی)" }
        ]}
      >
        {(item) => (
          <SelectItem key={item.key} value={item.key}>
            {item.label}
          </SelectItem>
        )}
      </Select>
    </div>
  );

  const renderPagination = () => {
    const totalPages = Math.ceil(totalDecisions / 30);
    
    return (
      <nav className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between items-center">
          <div className="text-sm text-gray-700">
            صفحه {page} از {totalPages} ({totalDecisions} مورد)
          </div>
          <div className="flex justify-end space-x-2 space-x-reverse">
            <Button
              onClick={() => setPage(p => p - 1)}
              isDisabled={page === 1}
              variant="bordered"
              className="text-default-600"
            >
              قبلی
            </Button>
            <Button
              onClick={() => setPage(p => p + 1)}
              isDisabled={page >= totalPages}
              variant="bordered"
              className="mr-3 text-default-600"
            >
              بعدی
            </Button>
          </div>
        </div>
      </nav>
    );
  };

  const columns = [
    { key: 'problem_name', header: 'نام تصمیم', render: (item) => (
      <div className="flex items-center gap-2">
        <span
          className="cursor-pointer text-blue-600 hover:text-blue-800"
          onClick={() => {
            const fullUrl = `${window.location.protocol}//${window.location.host}/problem/create?template_id=${item.problem_uid}`;
            navigator.clipboard.writeText(fullUrl);
            toast.success("لینک کامل با موفقیت کپی شد");
          }}
        >
          {item.problem_name}
        </span>
        {(item.category === 'public' || item.category === 'shared') && (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-green-600" title="عمومی">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0-3.933-2.185 2.25 2.25 0 0 0 3.933 2.185Z" />
          </svg>
        )}
      </div>
    )},
    { key: 'category', header: 'دسته‌بندی', render: (item) => (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.category === 'public' 
            ? 'bg-green-100 text-green-800' 
            : item.category === 'shared'
            ? 'bg-green-100 text-green-800'
            : item.category === 'private'
            ? 'bg-blue-100 text-blue-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {item.category === 'public' ? 'عمومی (ادمین)' : 
           item.category === 'shared' ? 'عمومی' : 
           item.category === 'private' ? 'خصوصی' : 
           item.category || 'خصوصی'}
        </span>
        <span className="text-xs text-gray-500">
          {item.category === 'public' ? 'قابل مشاهده برای همه' : 
           item.category === 'shared' ? 'قابل مشاهده برای همه' : 
           item.category === 'private' || !item.category ? 'فقط برای شما' : 
           'دسته‌بندی نامشخص'}
        </span>
      </div>
    )},
    { key: 'problem_uid', header: 'شناسه', render: (item) => (
      <span
        className="cursor-pointer font-mono"
        onClick={() => {
          const fullUrl = `${window.location.protocol}//${window.location.host}/problem/create?template_id=${item.problem_uid}`;
          navigator.clipboard.writeText(fullUrl);
          toast.success("لینک کامل با موفقیت کپی شد");
        }}
      >
        {item.problem_uid}
      </span>
    )},
    {
      key: 'created_at',
      header: 'تاریخ ایجاد',
      render: (item) => (
        <div className="tooltip-container relative group">
          <span>{getRelativeTime(item.created_at)}</span>
          <div className="tooltip absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
            {formatDateTime(item.created_at)}
          </div>
        </div>
      )
    }
  ];

  if (!requested) {
    return <Spinner size="lg" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 my-16">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-bold text-3xl">تصمیم‌های من</h1>
        <Button
          color="primary"
          variant="solid"
          as={Link}
          to="/problem/type"
        >
          ایجاد تصمیم جدید
        </Button>
      </div>

      {renderFilters()}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg"/>
        </div>
      ) : (
        <>
          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-right text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  {columns.map((column, index) => (
                    <th key={index} scope="col" className="px-6 py-3">
                      {column.header}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3">
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((decision, rowIndex) => (
                  <tr key={rowIndex} className={`border-b hover:bg-gray-50 ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        {column.render(decision)}
                      </td>
                    ))}
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          color="primary"
                          variant="light"
                          isIconOnly
                          onClick={() => handleViewResults(decision.problem_uid)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        </Button>
                        <Button
                          color="secondary"
                          variant="light"
                          isIconOnly
                          onClick={() => {
                            const fullUrl = `${window.location.protocol}//${window.location.host}/problem/create?template_id=${decision.problem_uid}`;
                            navigator.clipboard.writeText(fullUrl);
                            toast.success("لینک مستقیم با موفقیت کپی شد");
                          }}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                          </svg>
                        </Button>
                        <Button
                          color="danger"
                          variant="light"
                          isIconOnly
                          onClick={() => handleDelete(decision.problem_uid)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
} 